import { Link } from "react-router-dom";
import { useState } from "react";
import { Facebook, Instagram, Linkedin, Twitter, Youtube, Send } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LEGAL_PAGES } from "@/data/legal";
import { PRODUCTS } from "@/data/products";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message || "Subscribed");
      setEmail("");
    } catch {
      toast.error("Please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-neutral-950 text-neutral-300 mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-gold-300 to-gold-600 flex items-center justify-center text-neutral-950 font-black text-2xl font-display shadow-md ring-1 ring-gold-500/30">S</div>
              <div className="leading-none">
                <div className="font-display font-extrabold tracking-tight text-2xl text-white">SBC Ltd</div>
                <div className="mt-1.5 text-[11px] font-medium uppercase tracking-[0.28em] text-neutral-400">Scitygate Brewing Company</div>
              </div>
            </div>
            <p className="text-sm text-neutral-400 max-w-sm leading-relaxed">
              Premium beverages engineered in the United Kingdom, refreshing Africa and the world. Trusted quality. Modern brands. Global ambition.
            </p>
            <div>
              <div className="eyebrow mb-3">Newsletter</div>
              <form onSubmit={submit} className="flex gap-2 max-w-sm">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 bg-neutral-900 border border-neutral-800 rounded-full px-4 py-2 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:border-gold-400"
                  data-testid="newsletter-email-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-gold-400 hover:bg-gold-300 text-neutral-950 rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1 transition-colors disabled:opacity-50"
                  data-testid="newsletter-submit-button"
                >
                  <Send className="w-3.5 h-3.5" /> Subscribe
                </button>
              </form>
            </div>
            <div className="flex gap-3">
              {[Linkedin, Twitter, Instagram, Facebook, Youtube].map((Icon, i) => (
                <a key={i} href="#" className="w-9 h-9 rounded-full border border-neutral-800 flex items-center justify-center hover:border-gold-400 hover:text-gold-400 transition-colors" data-testid={`social-link-${i}`}>
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="eyebrow mb-4">Brands</div>
            <ul className="space-y-2 text-sm">
              {PRODUCTS.map((p) => (
                <li key={p.slug}><Link to={`/brands/${p.slug}`} className="hover:text-gold-400 transition-colors">{p.name}</Link></li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="eyebrow mb-4">Company</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-gold-400 transition-colors">About SBC</Link></li>
              <li><Link to="/sustainability" className="hover:text-gold-400 transition-colors">Sustainability</Link></li>
              <li><Link to="/quality" className="hover:text-gold-400 transition-colors">Quality</Link></li>
              <li><Link to="/news" className="hover:text-gold-400 transition-colors">News</Link></li>
              <li><Link to="/careers" className="hover:text-gold-400 transition-colors">Careers</Link></li>
              <li><Link to="/investors" className="hover:text-gold-400 transition-colors">Investors</Link></li>
              <li><Link to="/downloads" className="hover:text-gold-400 transition-colors">Downloads</Link></li>
              <li><Link to="/contact" className="hover:text-gold-400 transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="eyebrow mb-4">Business</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/distributor" className="hover:text-gold-400 transition-colors">Become a Distributor</Link></li>
              <li><Link to="/export-africa" className="hover:text-gold-400 transition-colors">Export to Africa</Link></li>
              <li><Link to="/portal/distributor" className="hover:text-gold-400 transition-colors">Distributor Portal</Link></li>
              <li><Link to="/portal/investor" className="hover:text-gold-400 transition-colors">Investor Portal</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="eyebrow mb-4">Contact</div>
            <address className="not-italic text-sm text-neutral-400 leading-relaxed">
              <span className="text-neutral-200 font-semibold">Scitygate Brewing Company Limited</span><br />
              2nd Floor Halifax House<br />
              93–101 Bridge Street<br />
              Manchester M3 2GX<br />
              United Kingdom
            </address>
            <a href="mailto:info@scitygate.com" className="mt-3 block text-sm hover:text-gold-400">info@scitygate.com</a>
            <div className="mt-3 text-[11px] text-neutral-500">
              Registered in England &amp; Wales · Company No. <span className="text-neutral-300 font-mono">17323558</span>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-neutral-900">
          <div className="eyebrow mb-4">Legal & Policies</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2">
            {LEGAL_PAGES.map((p) => (
              <Link key={p.slug} to={`/legal/${p.slug}`} className="text-xs text-neutral-500 hover:text-gold-400 transition-colors" data-testid={`legal-link-${p.slug}`}>
                {p.title}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-neutral-900 flex flex-col md:flex-row justify-between gap-4 text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} Scitygate Brewing Company Limited · Company No. 17323558 · Registered in England &amp; Wales.</div>
          <div>Crafted in Manchester. Refreshing the world.</div>
        </div>
      </div>
    </footer>
  );
}
